import { Component, computed, inject, input, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';
import { CwDatepicker } from '@bbraun/cortex-angular';
import { DatepickerLocale } from '@bbraun/cortex-web';
import { filter, startWith, switchMap, tap } from 'rxjs';
import { CustomShadowCssDirective } from '~/app/directives/CustomShadowCss';
import { LocaleService } from '~/app/services/locale.service';
import { generateUniqueId } from '~/app/utils/generateUniqueId';
import { getLocaleData } from './datepickerLocale';

type DatepickerValue = Date | string | Array<Date | string> | null;

@Component({
  selector: 'gplus-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  imports: [CwDatepicker, CustomShadowCssDirective]
})
export class DatepickerComponent {
  private readonly localeService: LocaleService = inject(LocaleService);

  public attrVef = input<string>();
  public control = input<AbstractControl>();
  public disabled = input<boolean>();
  public instanceId = input<string>(generateUniqueId());
  public label = input<string>();
  public mode = input<'single' | 'range'>('single');
  public enableTime = input<boolean>(false);
  public timeOnly = input<boolean>(false);
  public placeholder = input<string>();
  public readonly = input<boolean>();
  public required = input<boolean>();
  public validations = input<Record<string, any>>({});

  public readonly locale: Signal<Partial<DatepickerLocale & { dateFormat: string }>> = computed(() => {
    const localeData = getLocaleData(this.localeService.language()) as DatepickerLocale & {
      dateFormat: string;
    };
    if (!localeData) {
      return null;
    }

    const timeSuffix = this.enableTime() ? ' HH:mm' : '';

    return {
      ...localeData,
      dateFormat: localeData.dateFormat ? `${localeData.dateFormat}${timeSuffix}` : null
    };
  });

  // timeOnly bypasses the locale-derived format so the calendar's date part is dropped entirely
  public readonly dateFormat: Signal<string> = computed(() =>
    this.timeOnly() ? 'H:i' : (this.locale()?.dateFormat ?? null)
  );

  public error = signal<string>(null);

  public value = toSignal<Date>(
    toObservable(this.control).pipe(
      filter((control) => !!control),
      switchMap((control: AbstractControl) =>
        control.valueChanges.pipe(
          startWith(control.value),
          tap(() => {
            if (this.control().pristine) {
              this.error.set('');
            } else {
              this.error.set(
                Object.keys(this.validations()).reduce((err, key) => {
                  return err || (this.control().hasError(key) ? this.validations()[key] : null);
                }, null)
              );
            }
          })
        )
      )
    ),
    { initialValue: null }
  );

  public selected(inputtedValue: DatepickerValue) {
    if (!this.control()) {
      console.warn('No control provided for datepicker');
      return;
    }

    // markAsDirty() must run before setValue(): setValue() synchronously emits valueChanges,
    // which the error signal above reads via the still-pristine check. Marking dirty first
    // ensures that check (and any cross-field revalidation it triggers) sees the correct state.
    this.control().markAsDirty();
    this.control().setValue(inputtedValue);
  }
}
